// Sport & team reference data — used by the Sell form, browse filters, and any
// future validation. Kept in one file so both the create/edit UI and the filter
// UI stay in lockstep.
//
// TEAMS_BY_SPORT lists teams alphabetically. If the sport/category is listed in
// TEAMLESS_SPORTS the team input should not render at all (MMA/Wrestling fight
// under promotions, Pokemon/Disney don't have franchises, etc). Every non-teamless
// sport also supports an "Other — type in a name" option in the Sell form (for
// defunct franchises like Seattle Supersonics, minor-league teams, etc).

export const SPORTS_LIST = [
  "Baseball",
  "Basketball",
  "Football",
  "Football - College",
  "Hockey",
  "Soccer",
  "Racing",
  "MMA",
  "Wrestling",
  "Pokemon",
  "Disney",
  "Other",
];

export const TEAMLESS_SPORTS = new Set(["MMA", "Wrestling", "Pokemon", "Disney", "Other"]);

export const TEAMS_BY_SPORT = {
  Baseball: [
    "Arizona Diamondbacks", "Atlanta Braves", "Baltimore Orioles", "Boston Red Sox",
    "Chicago Cubs", "Chicago White Sox", "Cincinnati Reds", "Cleveland Guardians",
    "Colorado Rockies", "Detroit Tigers", "Houston Astros", "Kansas City Royals",
    "Los Angeles Angels", "Los Angeles Dodgers", "Miami Marlins", "Milwaukee Brewers",
    "Minnesota Twins", "New York Mets", "New York Yankees", "Oakland Athletics",
    "Philadelphia Phillies", "Pittsburgh Pirates", "San Diego Padres", "San Francisco Giants",
    "Seattle Mariners", "St. Louis Cardinals", "Tampa Bay Rays", "Texas Rangers",
    "Toronto Blue Jays", "Washington Nationals",
  ],
  Basketball: [
    "Atlanta Hawks", "Boston Celtics", "Brooklyn Nets", "Charlotte Hornets",
    "Chicago Bulls", "Cleveland Cavaliers", "Dallas Mavericks", "Denver Nuggets",
    "Detroit Pistons", "Golden State Warriors", "Houston Rockets", "Indiana Pacers",
    "Los Angeles Clippers", "Los Angeles Lakers", "Memphis Grizzlies", "Miami Heat",
    "Milwaukee Bucks", "Minnesota Timberwolves", "New Orleans Pelicans", "New York Knicks",
    "Oklahoma City Thunder", "Orlando Magic", "Philadelphia 76ers", "Phoenix Suns",
    "Portland Trail Blazers", "Sacramento Kings", "San Antonio Spurs", "Toronto Raptors",
    "Utah Jazz", "Washington Wizards",
  ],
  Football: [
    "Arizona Cardinals", "Atlanta Falcons", "Baltimore Ravens", "Buffalo Bills",
    "Carolina Panthers", "Chicago Bears", "Cincinnati Bengals", "Cleveland Browns",
    "Dallas Cowboys", "Denver Broncos", "Detroit Lions", "Green Bay Packers",
    "Houston Texans", "Indianapolis Colts", "Jacksonville Jaguars", "Kansas City Chiefs",
    "Las Vegas Raiders", "Los Angeles Chargers", "Los Angeles Rams", "Miami Dolphins",
    "Minnesota Vikings", "New England Patriots", "New Orleans Saints", "New York Giants",
    "New York Jets", "Philadelphia Eagles", "Pittsburgh Steelers", "San Francisco 49ers",
    "Seattle Seahawks", "Tampa Bay Buccaneers", "Tennessee Titans", "Washington Commanders",
  ],
  // NCAA Division I FBS Power-5 schools (union across ACC / Big Ten / Big 12 /
  // SEC / Pac-12 including realignment adds through 2024-2025) + Notre Dame
  // (independent). Alphabetical so the collector doesn't have to remember which
  // conference their school ended up in this year.
  "Football - College": [
    "Alabama Crimson Tide", "Arizona State Sun Devils", "Arizona Wildcats",
    "Arkansas Razorbacks", "Auburn Tigers", "Baylor Bears", "Boston College Eagles",
    "BYU Cougars", "California Golden Bears", "Cincinnati Bearcats", "Clemson Tigers",
    "Colorado Buffaloes", "Duke Blue Devils", "Florida Gators", "Florida State Seminoles",
    "Georgia Bulldogs", "Georgia Tech Yellow Jackets", "Houston Cougars",
    "Illinois Fighting Illini", "Indiana Hoosiers", "Iowa Hawkeyes", "Iowa State Cyclones",
    "Kansas Jayhawks", "Kansas State Wildcats", "Kentucky Wildcats",
    "Louisville Cardinals", "LSU Tigers", "Maryland Terrapins", "Miami Hurricanes",
    "Michigan State Spartans", "Michigan Wolverines", "Minnesota Golden Gophers",
    "Mississippi State Bulldogs", "Missouri Tigers", "NC State Wolfpack",
    "Nebraska Cornhuskers", "North Carolina Tar Heels", "Northwestern Wildcats",
    "Notre Dame Fighting Irish", "Ohio State Buckeyes", "Oklahoma Sooners",
    "Oklahoma State Cowboys", "Ole Miss Rebels", "Oregon Ducks", "Oregon State Beavers",
    "Penn State Nittany Lions", "Pittsburgh Panthers", "Purdue Boilermakers",
    "Rutgers Scarlet Knights", "SMU Mustangs", "South Carolina Gamecocks",
    "Stanford Cardinal", "Syracuse Orange", "TCU Horned Frogs", "Tennessee Volunteers",
    "Texas A&M Aggies", "Texas Longhorns", "Texas Tech Red Raiders", "UCF Knights",
    "UCLA Bruins", "USC Trojans", "Utah Utes", "Vanderbilt Commodores",
    "Virginia Cavaliers", "Virginia Tech Hokies", "Wake Forest Demon Deacons",
    "Washington Huskies", "Washington State Cougars", "West Virginia Mountaineers",
    "Wisconsin Badgers",
  ],
  Hockey: [
    "Anaheim Ducks", "Boston Bruins", "Buffalo Sabres", "Calgary Flames",
    "Carolina Hurricanes", "Chicago Blackhawks", "Colorado Avalanche", "Columbus Blue Jackets",
    "Dallas Stars", "Detroit Red Wings", "Edmonton Oilers", "Florida Panthers",
    "Los Angeles Kings", "Minnesota Wild", "Montreal Canadiens", "Nashville Predators",
    "New Jersey Devils", "New York Islanders", "New York Rangers", "Ottawa Senators",
    "Philadelphia Flyers", "Pittsburgh Penguins", "San Jose Sharks", "Seattle Kraken",
    "St. Louis Blues", "Tampa Bay Lightning", "Toronto Maple Leafs", "Utah Hockey Club",
    "Vancouver Canucks", "Vegas Golden Knights", "Washington Capitals", "Winnipeg Jets",
  ],
  Soccer: [
    // MLS
    "Atlanta United", "Austin FC", "CF Montréal", "Charlotte FC", "Chicago Fire",
    "Colorado Rapids", "Columbus Crew", "D.C. United", "FC Cincinnati", "FC Dallas",
    "Houston Dynamo", "Inter Miami CF", "LA Galaxy", "LAFC", "Minnesota United",
    "Nashville SC", "New England Revolution", "New York City FC", "New York Red Bulls",
    "Orlando City SC", "Philadelphia Union", "Portland Timbers", "Real Salt Lake",
    "San Jose Earthquakes", "Seattle Sounders", "Sporting Kansas City", "St. Louis City SC",
    "Toronto FC", "Vancouver Whitecaps",
    // Top international clubs collectors chase
    "Arsenal", "Barcelona", "Bayern Munich", "Chelsea", "Juventus", "Liverpool",
    "Manchester City", "Manchester United", "Paris Saint-Germain", "Real Madrid",
    "Tottenham Hotspur",
    // National teams
    "Argentina", "Brazil", "England", "France", "Germany", "Portugal", "Spain", "USA",
  ],
  Racing: [
    // F1 constructors
    "Alpine", "Aston Martin", "Ferrari", "Haas", "McLaren", "Mercedes", "Red Bull Racing",
    "Sauber", "Visa Cash App RB", "Williams",
    // NASCAR premier teams
    "23XI Racing", "Front Row Motorsports", "Hendrick Motorsports", "Joe Gibbs Racing",
    "RFK Racing", "Richard Childress Racing", "Stewart-Haas Racing", "Team Penske",
    "Trackhouse Racing",
  ],
};

// Sentinel string used by the Sell form's team dropdown when the seller picks
// "Other — type in name". NOT persisted to the DB — the free-text value the
// seller types replaces this in `form.team` before submission.
export const OTHER_TEAM_SENTINEL = "__OTHER__";
